import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import axiosInstance from '../../axiosInstance';
import { themeExpert } from '../../theme/expert';

/**
 * Relire un texte corrigé par l'IA avant de l'enregistrer.
 *
 * La correction n'écrase jamais directement : l'original reste affiché à côté,
 * et la proposition est modifiable. C'est l'expert qui signe le rapport.
 *
 * `fields` permet de corriger plusieurs champs d'un coup :
 * [{ key, label, value, plainText, spellingOnly }, …]. Sans `fields`, seul
 * `originalText` est corrigé — l'usage historique du rapport.
 */
function CorrectTextModal({ show, onHide, correctUrl, originalText = '',
                            onSave, fields }) {
  const [corrige, setCorrige] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [enregistre, setEnregistre] = useState(false);
  const [erreur, setErreur] = useState('');

  const zone = useRef(null);
  const zones = useRef({});

  const multiple = Array.isArray(fields) && fields.length > 0;

  const lire = (champ) => {
    const el = zones.current[champ.key];
    if (!el) return champ.value || '';
    return champ.plainText ? el.textContent.trim() : el.innerHTML;
  };

  const corriger = async () => {
    setOccupe(true);
    setErreur('');
    try {
      if (multiple) {
        await Promise.all(fields.map(async (champ) => {
          if (!champ.value) return;
          const { data } = await axiosInstance.post(correctUrl, {
            text: champ.value, spelling_only: Boolean(champ.spellingOnly),
          });
          const el = zones.current[champ.key];
          if (el) el.innerHTML = data.correction || '';
        }));
        setCorrige('fait');
      } else {
        const { data } = await axiosInstance.post(correctUrl,
                                                  { text: originalText });
        const html = data.correction || '';
        setCorrige(html);
        // Écrit dans la zone éditable sans re-rendu : un re-rendu replacerait
        // le curseur au début à chaque frappe.
        if (zone.current) zone.current.innerHTML = html;
      }
    } catch {
      setErreur("La correction n'a pas abouti. Le texte d'origine est intact.");
    } finally {
      setOccupe(false);
    }
  };

  const enregistrer = async () => {
    setEnregistre(true);
    setErreur('');
    try {
      if (multiple) {
        const valeurs = {};
        fields.forEach((champ) => { valeurs[champ.key] = lire(champ); });
        if (onSave) await onSave(valeurs);
      } else {
        const html = zone.current ? zone.current.innerHTML : corrige;
        if (!html) return;
        if (onSave) await onSave(html);
      }
      onHide();
    } catch {
      setErreur("L'enregistrement n'a pas abouti.");
    } finally {
      setEnregistre(false);
    }
  };

  useEffect(() => {
    if (show) return;
    setCorrige('');
    setErreur('');
    setOccupe(false);
    setEnregistre(false);
    if (zone.current) zone.current.innerHTML = '';
    Object.values(zones.current).forEach((el) => {
      if (el) el.innerHTML = '';
    });
  }, [show]);

  const cadre = {
    border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2,
    minHeight: 120, '& img': { maxWidth: '100%' },
  };

  return (
    <ThemeProvider theme={themeExpert}>
      <Dialog open={Boolean(show)} onClose={onHide} fullWidth maxWidth="lg">
        <DialogTitle>Relire la correction</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {erreur && <Alert severity="error">{erreur}</Alert>}

            <Box>
              <Button variant="contained" onClick={corriger} disabled={occupe}>
                {occupe ? 'Correction…' : "Lancer la correction par l'IA"}
              </Button>
              <Typography variant="caption" color="text.secondary"
                          component="div" sx={{ mt: 1 }}>
                Rien n'est enregistré tant que vous n'avez pas relu et validé.
              </Typography>
            </Box>

            {multiple ? fields.map((champ) => (
              <Box key={champ.key}>
                <Typography variant="h3" sx={{ mb: 1 }}>{champ.label}</Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary"
                                component="div" sx={{ mb: 0.5 }}>
                      Tel quel
                    </Typography>
                    <Box className="texte-riche" sx={{ ...cadre, minHeight: 70,
                               bgcolor: 'background.default' }}
                         dangerouslySetInnerHTML={{ __html: champ.value || '' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary"
                                component="div" sx={{ mb: 0.5 }}>
                      Proposition — modifiable
                    </Typography>
                    <Box
                      className="texte-riche"
                      ref={(el) => { zones.current[champ.key] = el; }}
                      contentEditable
                      suppressContentEditableWarning
                      sx={{ ...cadre, minHeight: 70,
                            bgcolor: 'success.light', opacity: 0.95 }}
                    />
                  </Box>
                </Stack>
              </Box>
            )) : (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary"
                              component="div" sx={{ mb: 0.5 }}>
                    Texte d'origine
                  </Typography>
                  <Box className="texte-riche" sx={{ ...cadre, bgcolor: 'background.default' }}
                       dangerouslySetInnerHTML={{ __html: originalText || '' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary"
                              component="div" sx={{ mb: 0.5 }}>
                    Proposition — modifiable
                  </Typography>
                  <Box
                    className="texte-riche"
                    ref={zone}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => setCorrige(e.currentTarget.innerHTML)}
                    sx={{ ...cadre, bgcolor: 'background.paper' }}
                  />
                  {!corrige && (
                    <Typography variant="caption" color="text.secondary"
                                component="div" sx={{ mt: 1 }}>
                      Lancez la correction pour voir une proposition ici.
                    </Typography>
                  )}
                </Box>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onHide} disabled={enregistre}>Fermer</Button>
          <Button variant="contained" onClick={enregistrer}
                  disabled={enregistre || !corrige}>
            {enregistre ? 'Enregistrement…' : 'Enregistrer la correction'}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default CorrectTextModal;
